import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PhotoGrid from './components/PhotoGrid';
import Lightbox from './components/Lightbox';
import { usePortfolioStore } from '../../hooks/usePortfolioStore';

export default function PortfolioPage() {
  const { items, loading } = usePortfolioStore();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';
    const pageUrl = `${siteUrl}/portfolio`;

    // Title
    const prevTitle = document.title;
    document.title = '시공 사례 포트폴리오 | 라인 인테리어 페인트·도장·방수';

    // Canonical
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = pageUrl;
    canonical.id = 'portfolio-canonical';
    document.head.appendChild(canonical);

    // Meta description override
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute('content') ?? '';
    desc?.setAttribute(
      'content',
      '라인 인테리어의 페인트 시공, 도장, 방수 공사 시공 사례를 확인하세요. 15년 경력 전문 기술자의 고품질 마감 작업을 포트폴리오로 만나보세요.'
    );

    // og:url
    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.setAttribute('content', pageUrl);
    ogUrl.id = 'portfolio-og-url';
    document.head.appendChild(ogUrl);

    // JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'portfolio-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': pageUrl,
          url: pageUrl,
          name: '시공 사례 포트폴리오 | 라인 인테리어',
          description:
            '라인 인테리어의 페인트 시공, 도장, 방수 공사 시공 사례 포트폴리오. 15년 경력 전문 기술자의 고품질 마감 작업을 확인하세요.',
          isPartOf: { '@id': `${siteUrl}/#website` },
          inLanguage: 'ko-KR',
          dateModified: '2026-03-26',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '홈',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '포트폴리오',
                item: pageUrl,
              },
            ],
          },
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      desc?.setAttribute('content', prevDesc);
      document.getElementById('portfolio-canonical')?.remove();
      document.getElementById('portfolio-og-url')?.remove();
      document.getElementById('portfolio-jsonld')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">

          {/* Page Header */}
          <div className="mb-12 border-b border-stone-100 pb-10">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-stone-400 mb-3">
              Line Interior
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
              <h1
                className="text-5xl md:text-6xl font-bold text-stone-900 leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Portfolio
              </h1>
              <Link
                to="/portfolio/manage"
                className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition-colors cursor-pointer border-b border-stone-300 pb-0.5 whitespace-nowrap self-start sm:self-auto"
              >
                <i className="ri-settings-3-line" />
                사진 관리
              </Link>
            </div>
          </div>

          {/* Content Area */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-48">
              <div className="w-8 h-8 border-2 border-stone-200 border-t-stone-700 rounded-full animate-spin mb-4" />
              <p className="text-stone-400 text-sm">불러오는 중...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-48">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-stone-100 mb-5">
                <i className="ri-image-2-line text-2xl text-stone-400" />
              </div>
              <p className="text-stone-700 font-medium text-base mb-1">
                아직 등록된 포트폴리오가 없습니다
              </p>
              <p className="text-stone-400 text-sm mb-6">사진 관리에서 시공 사례를 추가해보세요</p>
              <Link
                to="/portfolio/manage"
                className="px-7 py-3 bg-stone-900 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-stone-700 transition-colors whitespace-nowrap"
              >
                사진 추가하기
              </Link>
            </div>
          ) : (
            <PhotoGrid items={items} onClickItem={setLightboxIndex} />
          )}
        </div>
      </main>

      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          items={items}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}
    </div>
  );
}
